/**
 * Bundle 175 - Force Check In All dialog (super-admin only).
 *
 * Emergency unblock when scan-tickets is unavailable. Server
 * re-validates count, confirmation phrase, and admin email before
 * running one `updateMany` on eligible rows.
 */
import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, ShieldAlert, AlertTriangle } from 'lucide-react';
import api from '@/utils/api';

const ForceCheckInAllDialog = ({ open, onClose, event, adminEmail,
                                   registrations, onSuccess }) => {
  const [phrase, setPhrase] = useState('');
  const [emailConfirm, setEmailConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // Bundle 178 - server-authoritative counts. FE no longer computes
  // eligibility client-side (that was the Bundle 175 filter miss).
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    if (!open || !event?.id) return undefined;
    let cancelled = false;
    setPreviewLoading(true);
    setPreview(null);
    (async () => {
      try {
        const { data } = await api.get(
          `/admin/events/${event.id}/force-check-in-preview`);
        if (!cancelled) setPreview(data);
      } catch (e) {
        if (!cancelled) {
          const d = e?.response?.data?.detail;
          setError((d && (d.message || JSON.stringify(d)))
            || 'Could not load eligibility preview.');
        }
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, event?.id]);

  const eligibleCount = preview?.eligible_count ?? 0;
  const excluded = preview?.excluded || {};
  const excludedBuckets = [
    ['no_payment_confirmed', 'no payment confirmed'],
    ['cancelled_or_rejected', 'cancelled or rejected'],
    ['draft_or_pending', 'draft or pending'],
    ['already_checked_in', 'already checked in'],
  ].filter(([k]) => (excluded[k] || 0) > 0);

  const expectedPhrase = `CHECK IN ALL ${eligibleCount} GUESTS`;
  const phraseOk = phrase.trim() === expectedPhrase;
  const emailOk = emailConfirm.trim().toLowerCase()
    === (adminEmail || '').trim().toLowerCase();

  useEffect(() => { if (open) { setPhrase(''); setEmailConfirm('');
    setError(''); } }, [open]);

  const submit = async () => {
    setBusy(true);
    setError('');
    try {
      const { data } = await api.post(
        `/admin/events/${event.id}/registrations/force-check-in-all`,
        {
          expected_count: eligibleCount,
          confirmation_phrase: phrase.trim(),
          admin_email: emailConfirm.trim(),
        },
      );
      onSuccess?.(data);
      onClose?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(
        (detail && (detail.message || JSON.stringify(detail)))
        || 'Force check-in failed.',
      );
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent
        className="sm:max-w-[560px]"
        data-testid="force-check-in-all-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-red-600" />
            Force check in all eligible registrations for {event?.title}
          </DialogTitle>
          <DialogDescription>
            Super-admin emergency unblock. Use ONLY if scan-tickets is
            broken.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div
            className="rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-900"
            role="status"
          >
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <div className="text-xs">
                This is irreversible for this session. Only use if the
                scan-tickets flow is broken and you cannot check
                guests in individually.
              </div>
            </div>
          </div>
          <div
            className="rounded-md border border-neutral-200 bg-neutral-50 p-3 text-xs space-y-1"
            data-testid="force-check-in-preview-block"
          >
            <div>
              Eligible to check in:{' '}
              <span
                className="font-bold text-neutral-900"
                data-testid="force-check-in-eligible-count"
              >{previewLoading ? '...' : eligibleCount}</span>
            </div>
            {previewLoading ? (
              <div className="text-neutral-500">Loading counts...</div>
            ) : excludedBuckets.length > 0 ? (
              <div
                className="text-neutral-500"
                data-testid="force-check-in-excluded-breakdown"
              >
                Excluded:{' '}
                {excludedBuckets.map(([k, label], idx) => (
                  <span
                    key={k}
                    data-testid={`force-check-in-excluded-${k}`}
                  >
                    {excluded[k]} {label}
                    {idx < excludedBuckets.length - 1 ? ', ' : ''}
                  </span>
                ))}
              </div>
            ) : (
              <div className="text-neutral-500">
                Nothing excluded.
              </div>
            )}
          </div>
          {/* Bundle 179 - friendly explanation when the reason for
              zero eligible is that everyone is already checked in.
              Removes the "did the button break again?" confusion. */}
          {!previewLoading
             && eligibleCount === 0
             && (excluded.already_checked_in || 0) > 0
             && (excluded.no_payment_confirmed || 0) === 0
             && (excluded.draft_or_pending || 0) === 0
             && (excluded.cancelled_or_rejected || 0) === 0 ? (
            <div
              className="rounded-md border border-emerald-300 bg-emerald-50 p-3 text-xs text-emerald-900"
              role="status"
              data-testid="force-check-in-all-done"
            >
              Everyone eligible is already checked in. Nothing to do
              here.
            </div>
          ) : null}
          <div>
            <label
              htmlFor="fcia-phrase"
              className="block text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-1"
            >
              Type exactly:{' '}
              <code className="text-neutral-800">{expectedPhrase}</code>
            </label>
            <Input
              id="fcia-phrase"
              value={phrase}
              onChange={(e) => setPhrase(e.target.value)}
              data-testid="force-check-in-phrase"
              placeholder={expectedPhrase}
            />
            <div className="text-xs mt-1">
              {phraseOk ? (
                <span className="text-green-700">Match confirmed.</span>
              ) : (
                <span className="text-neutral-500">
                  Phrase must match exactly (case-sensitive).
                </span>
              )}
            </div>
          </div>
          <div>
            <label
              htmlFor="fcia-email"
              className="block text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-1"
            >
              Type your admin email to confirm
            </label>
            <Input
              id="fcia-email"
              value={emailConfirm}
              onChange={(e) => setEmailConfirm(e.target.value)}
              data-testid="force-check-in-email"
              placeholder={adminEmail}
            />
          </div>
          {error ? (
            <div
              className="rounded-md border border-red-300 bg-red-50 p-2 text-xs text-red-900"
              role="alert"
              data-testid="force-check-in-error"
            >{error}</div>
          ) : null}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={busy}
            data-testid="force-check-in-cancel"
          >Cancel</Button>
          <Button
            onClick={submit}
            disabled={busy || !phraseOk || !emailOk || eligibleCount === 0}
            className="bg-red-700 hover:bg-red-800 text-white"
            data-testid="force-check-in-confirm"
          >
            {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    : null}
            Force check in {eligibleCount} guests
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ForceCheckInAllDialog;
